/**
 * True when error is 401 / token expired. Caller should skip upload-failure toasts and let the API interceptor handle redirect + session-expired toast.
 */
export function isAuthError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const ax = err as { response?: { status?: number }; message?: string };
    if (ax.response?.status === 401) return true;
  }
  if (err instanceof Error && err.message) {
    const m = err.message.toLowerCase();
    if (m.includes('token expired') || m.includes('401') || m.includes('unauthorized')) return true;
  }
  return false;
}

/**
 * Parse upload/API errors to detect rate limit and get user-facing message.
 * Handles both fetch (Error with JSON body) and axios (response.data.detail) shapes.
 */
export function getRateLimitFromError(err: unknown): { isRateLimit: boolean; message: string } {
  const fallback = { isRateLimit: false, message: '' };

  // Axios-style: error.response.data.detail
  if (err && typeof err === 'object' && 'response' in err) {
    const ax = err as { response?: { status?: number; data?: { detail?: unknown } } };
    const detail = ax.response?.data?.detail;
    if (ax.response?.status === 429 && detail && typeof detail === 'object' && 'error' in detail) {
      const d = detail as { error?: string; message?: string };
      if (d.error === 'rate_limit_exceeded') {
        return {
          isRateLimit: true,
          message: typeof d.message === 'string' ? d.message : 'Upload limit exceeded for this period.',
        };
      }
    }
    return fallback;
  }

  // Fetch-style: Error with message that might be JSON body
  if (err instanceof Error && err.message) {
    try {
      const parsed = JSON.parse(err.message);
      const detail = parsed?.detail ?? parsed;
      if (detail && typeof detail === 'object' && detail.error === 'rate_limit_exceeded') {
        return {
          isRateLimit: true,
          message: typeof detail.message === 'string' ? detail.message : 'Upload limit exceeded for this period.',
        };
      }
    } catch {
      // not JSON
    }
  }

  return fallback;
}

export const RATE_LIMIT_EXCEEDED_EVENT = 'app:rate_limit_exceeded';

export function dispatchRateLimitExceeded(message: string): void {
  window.dispatchEvent(new CustomEvent(RATE_LIMIT_EXCEEDED_EVENT, { detail: { message } }));
}
